// src/components/Dashboard.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { Info } from "lucide-react";

// Import CSS, fonts, Bootstrap, etc.
import "../fonts/bootstrap-icons.woff";
import "../fonts/bootstrap-icons.woff2";
import "bootstrap/dist/css/bootstrap.min.css";
import "../css/main.css";
import "../css/bootstrap-icons.css";
import "bootstrap/dist/js/bootstrap.bundle";
import "jquery";

// Import Firebase Auth, Firestore, and Storage
import { auth, db, storage } from "../firebase";

///////////////////////////
// Firebase Storage Upload //
///////////////////////////
async function uploadProfilePicture(file, uid) {
  try {
    const fileRef = storage.ref().child(`profilePictures/${uid}/${file.name}-${Date.now()}`);
    await fileRef.put(file);
    const downloadURL = await fileRef.getDownloadURL();
    return downloadURL;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
}

//////////////////////
// Dashboard Header //
//////////////////////
function DashboardHeader({ user }) {
  return (
    <header className="site-header d-flex flex-column justify-content-center align-items-center">
      <div className="container">
        <div className="row align-items-center">
          <div className="col-lg-5 col-12">
            <h2 className="text-white">Dashboard</h2>
            <p className="text-white mb-0">
              Welcome, {user.displayName ? user.displayName : user.email}!
            </p>
          </div>
        </div>
      </div>
    </header>
  );
}

////////////////////////
// Profile Settings   //
////////////////////////
function ProfileSettings({ user, refreshUserData }) {
  const [profileData, setProfileData] = useState({
    displayName: "",
    email: "",
    phone: "",
    birthday: "",
    photoURL: ""
  });
  const [password, setPassword] = useState(""); // For password update
  const [newProfilePic, setNewProfilePic] = useState(null);
  const [message, setMessage] = useState("");

  // Fetch profile info from Firestore ("front-users")
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const doc = await db.collection("front-users").doc(user.uid).get();
        if (doc.exists) {
          const data = doc.data();
          setProfileData({
            displayName: data.displayName || "",
            email: data.email || user.email,
            phone: data.phone || "",
            birthday: data.birthday || "",
            photoURL: data.photoURL || ""
          });
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };
    fetchProfile();
  }, [user.uid, user.email]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setNewProfilePic(e.target.files[0]);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setMessage("");

    try {
      let updatedPhotoURL = profileData.photoURL;
      if (newProfilePic) {
        // Upload the new profile picture and get its URL from Firebase Storage
        updatedPhotoURL = await uploadProfilePicture(newProfilePic, user.uid);
      }

      // Update the user's document in "front-users"
      await db.collection("front-users").doc(user.uid).update({
        displayName: profileData.displayName,
        phone: profileData.phone,
        birthday: profileData.birthday,
        photoURL: updatedPhotoURL
      });

      // Optionally update Firebase Auth email and password if changed
      if (profileData.email !== user.email) {
        await user.updateEmail(profileData.email);
      }
      if (password.trim() !== "") {
        await user.updatePassword(password);
      }

      setMessage("Profile updated successfully!");
      refreshUserData(); // Optionally refresh user context in your app
    } catch (error) {
      console.error("Error updating profile:", error);
      setMessage("An error occurred while updating your profile. " + error.message);
    }
  };

  return (
    <div className="p-4">
      <h4>Profile Settings</h4>
      {message && <div className="alert alert-info">{message}</div>}
      <form onSubmit={handleSave}>
        <div className="mb-3">
          <label className="form-label" htmlFor="photoURL">
            Profile Picture
          </label>
          <div className="d-flex align-items-center">
            <img
              src={
                newProfilePic
                  ? URL.createObjectURL(newProfilePic)
                  : (profileData.photoURL || "/default-profile.png")
              }
              alt="Profile"
              className="rounded-circle me-3"
              width="80"
              height="80"
            />
            <input type="file" accept="image/*" onChange={handleFileChange} />
          </div>
        </div>
        <div className="mb-3">
          <label className="form-label" htmlFor="displayName">
            Name
          </label>
          <input
            type="text"
            id="displayName"
            name="displayName"
            className="form-control"
            value={profileData.displayName}
            onChange={handleChange}
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label" htmlFor="email">
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            className="form-control"
            value={profileData.email}
            onChange={handleChange}
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label" htmlFor="password">
            New Password (leave blank to keep current)
          </label>
          <input
            type="password"
            id="password"
            name="password"
            className="form-control"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <div className="mb-3">
          <label className="form-label" htmlFor="phone">
            Phone Number
          </label>
          <input
            type="text"
            id="phone"
            name="phone"
            className="form-control"
            value={profileData.phone}
            onChange={handleChange}
          />
        </div>
        <div className="mb-3">
          <label className="form-label" htmlFor="birthday">
            Birthday
          </label>
          <input
            type="date"
            id="birthday"
            name="birthday"
            className="form-control"
            value={profileData.birthday}
            onChange={handleChange}
          />
        </div>
        <button type="submit" className="btn custom-btn">
          Save Changes
        </button>
      </form>
    </div>
  );
}

//////////////////////////
// Payment Methods Tab  //
//////////////////////////
// function PaymentMethods({ user }) {
//   const [methods, setMethods] = useState([]);
//   const [newMethod, setNewMethod] = useState({
//     cardName: "",
//     cardNumber: "",
//     expiryDate: "",
//     cvv: ""
//   });
//   const [message, setMessage] = useState("");

//   // Fetch payment methods for the current user from Firestore
//   useEffect(() => {
//     const fetchMethods = async () => {
//       try {
//         const snapshot = await db
//           .collection("paymentMethods")
//           .where("userUID", "==", user.uid)
//           .get();
//         const methodsData = snapshot.docs.map((doc) => ({
//           id: doc.id,
//           ...doc.data()
//         }));
//         setMethods(methodsData);
//       } catch (error) {
//         console.error("Error fetching payment methods:", error);
//       }
//     };
//     fetchMethods();
//   }, [user.uid]);

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setNewMethod((prev) => ({ ...prev, [name]: value }));
//   };

//   const handleAddMethod = async (e) => {
//     e.preventDefault();
//     // Basic form validation
//     if (
//       !newMethod.cardName ||
//       !newMethod.cardNumber ||
//       !newMethod.expiryDate ||
//       !newMethod.cvv
//     ) {
//       setMessage("Please fill out all payment fields.");
//       return;
//     }

//     try {
//       const methodData = {
//         ...newMethod,
//         userUID: user.uid,
//         uniqueID: uuidv4(), // Unique identifier for this card
//         timestamp: Date.now()
//       };
//       const docRef = await db.collection("paymentMethods").add(methodData);
//       setMethods((prev) => [...prev, { id: docRef.id, ...methodData }]);
//       setNewMethod({ cardName: "", cardNumber: "", expiryDate: "", cvv: "" });
//       setMessage("Payment method added successfully.");
//       setTimeout(() => setMessage(""), 3000);
//     } catch (error) {
//       console.error("Error adding payment method:", error);
//       setMessage("An error occurred. Please try again.");
//     }
//   };

//   return (
//     <div className="p-4">
//       <h4>Payment Methods</h4>
//       {methods.length === 0 ? (
//         <p>No payment methods saved.</p>
//       ) : (
//         <ul className="list-group mb-3">
//           {methods.map((method) => (
//             <li key={method.id} className="list-group-item">
//               <strong>{method.cardName}</strong> - **** **** ****{" "}
//               {method.cardNumber.slice(-4)} | Expires: {method.expiryDate}
//             </li>
//           ))}
//         </ul>
//       )}
//       {message && <div className="alert alert-success">{message}</div>}
//       <form onSubmit={handleAddMethod}>
//         <div className="mb-3">
//           <label className="form-label" htmlFor="cardName">
//             Name on Card
//           </label>
//           <input
//             type="text"
//             id="cardName"
//             name="cardName"
//             className="form-control"
//             placeholder="Name as it appears on the card"
//             value={newMethod.cardName}
//             onChange={handleInputChange}
//             required
//           />
//         </div>
//         <div className="mb-3">
//           <label className="form-label" htmlFor="cardNumber">
//             Card Number
//           </label>
//           <input
//             type="text"
//             id="cardNumber"
//             name="cardNumber"
//             className="form-control"
//             placeholder="Enter card number"
//             value={newMethod.cardNumber}
//             onChange={handleInputChange}
//             required
//           />
//         </div>
//         <div className="row mb-3">
//           <div className="col-md-6">
//             <label className="form-label" htmlFor="expiryDate">
//               Expiry Date
//             </label>
//             <input
//               type="text"
//               id="expiryDate"
//               name="expiryDate"
//               className="form-control"
//               placeholder="MM/YY"
//               value={newMethod.expiryDate}
//               onChange={handleInputChange}
//               required
//             />
//           </div>
//           <div className="col-md-6">
//             <label className="form-label" htmlFor="cvv">
//               CVV
//             </label>
//             <input
//               type="text"
//               id="cvv"
//               name="cvv"
//               className="form-control"
//               placeholder="Enter CVV"
//               value={newMethod.cvv}
//               onChange={handleInputChange}
//               required
//             />
//           </div>
//         </div>
//         <button type="submit" className="btn custom-btn">
//           Add Payment Method
//         </button>
//       </form>
//     </div>
//   );
// }

//////////////////////////
// Contact Support Tab  //
//////////////////////////
function ContactSupport({ user }) {
  const [supportData, setSupportData] = useState({
    subject: "",
    message: ""
  });
  const [status, setStatus] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSupportData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!supportData.subject || !supportData.message) return;
    try {
      await db.collection("supportRequests").add({
        userUID: user.uid,
        email: user.email,
        subject: supportData.subject,
        message: supportData.message,
        timestamp: Date.now()
      });
      setStatus("Your support request has been submitted. We will get back to you soon.");
      setSupportData({ subject: "", message: "" });
    } catch (error) {
      console.error("Error submitting support request:", error);
      setStatus("An error occurred. Please try again later.");
    }
  };

  return (
    <div className="p-4">
      <h4>Contact Support</h4>
      {status && <div className="alert alert-info">{status}</div>}
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label" htmlFor="subject">
            Subject
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            className="form-control"
            value={supportData.subject}
            onChange={handleChange}
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label" htmlFor="message">
            Message
          </label>
          <textarea
            id="message"
            name="message"
            className="form-control"
            value={supportData.message}
            onChange={handleChange}
            required
          ></textarea>
        </div>
        <button type="submit" className="btn custom-btn">
          Send Request
        </button>
      </form>
    </div>
  );
}

////////////////////////////
// Plan Details Component //
////////////////////////////

function PlanDetails({ user }) {
  const [plan, setPlan] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(true);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    // Query the purchases collection for the user's most recent purchase
    const fetchPlan = async () => {
      try {
        const querySnapshot = await db
          .collection("purchases")
          .where("email", "==", user.email)
          .orderBy("timestamp", "desc")
          .limit(1)
          .get();
        if (!querySnapshot.empty) {
          const doc = querySnapshot.docs[0];
          setPlan({ id: doc.id, ...doc.data() });
        }
      } catch (error) {
        console.error("Error fetching plan details:", error);
      } finally {
        setLoadingPlan(false);
      }
    };
    fetchPlan();
  }, [user.email]);

  
  return (
    <div className="p-4">
      <h4>Your Plan Details</h4>
      {loadingPlan ? (
        <p>Loading plan details...</p>
      ) : plan ? (
        <div>
          <p>
            <strong>Plan:</strong> {plan.plan}
          </p>
          <p>
            <strong>Price:</strong>{" "}
            {plan.plan === "basic"
              ? "$15"
              : plan.plan === "plus"
              ? "$30"
              : plan.plan === "premium"
              ? "$50"
              : "N/A"}
          </p>
          <p>
            <strong>Uses Left:</strong> {plan.usesLeft}
          </p>
          <p className="flex items-center gap-2">
            
          <strong>Session Tag:</strong> 
          <a href={`https://dashboard.nutriplan.seif8911.xyz/login?purchase-successful&session=${plan.sessionTag}`}>
          {plan.sessionTag}
            </a> 
            <span 
              className="focus:outline-none sessiontag" 
              onClick={() => setShowInfo(!showInfo)}
            >
              <Info size={16} className="text-gray-500 cursor-pointer" />
            </span>
            <br></br>
            {showInfo && (
              <span className="ml-2 p-2 bg-gray-100 border border-gray-300 rounded shadow-md text-sm">
                if you have lost access to the dashboard, you can click on it to regain aaccess and use up one of its uses. This is a special token, used only by you to login into the dashboard, granting this token can give COMPLETE access over your plan and subscription. incase of it getting stolen please contact support immediately
              </span>
            )}
          </p>
          <p>
            <strong>Purchased On:</strong>{" "}
            {new Date(plan.timestamp).toLocaleString()}
          </p>
          <button className="btn btn-warning" onClick={() => window.location.href = "/plans"}>
            Upgrade / Change Plan
          </button>
        </div>
      ) : (
        <div>
          <p>You have not purchased a plan yet.</p>
          <button className="btn custom-btn" onClick={() => window.location.href = "/plans"}>
            Purchase a Plan
          </button>
        </div>
      )}
    </div>
  );
}

////////////////////////////
// Main Dashboard Section //
////////////////////////////
export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState("profile");
  const [loading, setLoading] = useState(true);

  // Check if the user is authenticated
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      if (currentUser) {
        setUser(currentUser);
      } else {
        navigate("/login");
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  if (loading) return <div>Loading dashboard...</div>;

  return (
    <div>
      <DashboardHeader user={user} />
      <div className="container my-5">
        <ul className="nav nav-tabs">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "profile" ? "active" : ""}`}
              onClick={() => setActiveTab("profile")}
            >
              Profile Settings
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "plan" ? "active" : ""}`}
              onClick={() => setActiveTab("plan")}
            >
              Plan Details
            </button>
          </li>
          {/* <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "payment" ? "active" : ""}`}
              onClick={() => setActiveTab("payment")}
            >
              Payment Methods
            </button>
          </li> */}
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "support" ? "active" : ""}`}
              onClick={() => setActiveTab("support")}
            >
              Contact Support
            </button>
          </li>
        </ul>

        <div className="tab-content border p-4">
          {activeTab === "profile" && (
            <ProfileSettings user={user} refreshUserData={() => {}} />
          )}
          {activeTab === "plan" && <PlanDetails user={user} />}
          {activeTab === "support" && <ContactSupport user={user} />}
        </div>
      </div>
    </div>
  );
}
